/**
 * TenantMaintenance — Tenant maintenance request page.
 *
 * Tenants can:
 *   - View all open/resolved tickets for their property
 *   - Submit a new maintenance request via a modal
 *
 * Data: fetched directly from Supabase using the tenant's tenant_link.
 */

import { useEffect, useRef, useState } from 'react';
import { Wrench, Plus, X, Loader2, Camera, ImagePlus } from 'lucide-react';
import { useAppContext } from '../../context/useAppContext';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/formatters';
import { createNotification } from '../../utils/notifications';

interface Ticket {
    id: string;
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    status: 'new' | 'in_progress' | 'resolved';
    created_at: string;
    property_id: string;
}

export default function TenantMaintenance() {
    const { user } = useAppContext();

    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [propertyId, setPropertyId] = useState<string | null>(null);
    const [propertyName, setPropertyName] = useState('');
    const [loading, setLoading] = useState(true);
    const [noLink, setNoLink] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
    const [photoFiles, setPhotoFiles] = useState<File[]>([]);
    const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchData = async () => {
        if (!user.id && !user.email) return;
        try {
            setLoading(true);

            // Resolve tenant_link → property_id
            let q = supabase.from('tenant_links').select('id, property_id');
            if (user.id) q = q.eq('tenant_user_id', user.id);
            else q = q.eq('tenant_email', user.email);

            const { data: linkData } = await q.maybeSingle();

            if (!linkData) {
                setNoLink(true);
                setLoading(false);
                return;
            }

            setPropertyId(linkData.property_id);

            // Property name for subtitle
            const { data: propData } = await supabase
                .from('properties')
                .select('name')
                .eq('id', linkData.property_id)
                .single();
            setPropertyName(propData?.name ?? '');

            // Maintenance tickets for this property
            const { data: ticketData, error } = await supabase
                .from('maintenance_tickets')
                .select('*')
                .eq('property_id', linkData.property_id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setTickets(ticketData || []);
        } catch (err) {
            console.error('[TenantMaintenance] fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [user.id, user.email]);

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (!files.length) return;
        setPhotoFiles((prev) => [...prev, ...files]);
        files.forEach((f) => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setPhotoPreviews((prev) => [...prev, ev.target?.result as string]);
            };
            reader.readAsDataURL(f);
        });
        // reset so the same file can be selected again
        e.target.value = '';
    };

    const removePhoto = (idx: number) => {
        setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
        setPhotoPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !propertyId || !user.id) return;
        setIsSubmitting(true);
        try {
            const { data: ticket, error } = await supabase
                .from('maintenance_tickets')
                .insert({
                    title,
                    description,
                    property_id: propertyId,
                    created_by: user.id,
                    status: 'new',
                    priority,
                    created_at: new Date().toISOString(),
                })
                .select()
                .single();
            if (error) throw error;

            // Upload photos
            for (const file of photoFiles) {
                const ext = file.name.split('.').pop() ?? 'jpg';
                const path = `${ticket.id}/${Date.now()}.${ext}`;
                const { error: uploadErr } = await supabase.storage
                    .from('ticket-images')
                    .upload(path, file, { upsert: false });
                if (uploadErr) {
                    console.error('Photo upload error:', uploadErr);
                    continue;
                }
                await supabase.from('ticket_images').insert({
                    ticket_id: ticket.id,
                    uploaded_by: user.id,
                    storage_path: path,
                });
            }

            // Notify landlord: find the property owner
            const { data: propData } = await supabase
                .from('properties')
                .select('owner_id, name')
                .eq('id', propertyId)
                .single();
            if (propData?.owner_id) {
                await createNotification(
                    propData.owner_id,
                    'ticket_created',
                    'New Maintenance Request',
                    `${title} — ${propData.name}`,
                    '/app/landlord/maintenance'
                );
            }

            setIsModalOpen(false);
            setTitle('');
            setDescription('');
            setPriority('medium');
            setPhotoFiles([]);
            setPhotoPreviews([]);
            fetchData();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Unknown error';
            alert('Error submitting request: ' + msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <Loader2 className="animate-spin" size={40} color="var(--color-primary)" />
            </div>
        );
    }

    if (noLink) {
        return (
            <div className="page" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <Wrench size={40} color="var(--color-text-tertiary)" style={{ margin: '0 auto 1.5rem', display: 'block' }} />
                <h2 style={{ margin: '0 0 0.75rem' }}>No property linked yet</h2>
                <p style={{ color: 'var(--color-text-secondary)', maxWidth: 380, marginInline: 'auto', lineHeight: 1.6 }}>
                    Your account is not linked to a property. Contact your landlord to get started.
                </p>
            </div>
        );
    }

    const openCount = tickets.filter((t) => t.status !== 'resolved').length;

    return (
        <div className="page">
            {/* Page header */}
            <div className="page__header" style={{ marginBottom: '2rem' }}>
                <div>
                    <h1 className="page__title">Maintenance Requests</h1>
                    <p className="page__subtitle">
                        {openCount > 0
                            ? `${openCount} open ticket${openCount > 1 ? 's' : ''}`
                            : 'All issues resolved'} · {propertyName}
                    </p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => setIsModalOpen(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <Plus size={16} /> Report Issue
                </button>
            </div>

            {/* Ticket list */}
            <div className="data-panel">
                {tickets.length === 0 ? (
                    <div style={{ padding: '5rem 2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                        <Wrench size={36} color="var(--color-text-tertiary)" style={{ margin: '0 auto 1rem', display: 'block' }} />
                        <p style={{ margin: 0 }}>No maintenance requests yet. Use the button above to report an issue.</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Issue</th>
                                <th>Reported</th>
                                <th>Priority</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tickets.map((ticket) => (
                                <tr key={ticket.id}>
                                    <td style={{ fontWeight: 500 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Wrench size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0 }} />
                                            {ticket.title}
                                        </div>
                                        {ticket.description && (
                                            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '320px', whiteSpace: 'nowrap' }}>
                                                {ticket.description}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ color: 'var(--color-text-secondary)' }}>
                                        {formatDate(ticket.created_at)}
                                    </td>
                                    <td>
                                        {ticket.priority === 'high' && <span className="badge badge--red">Emergency</span>}
                                        {ticket.priority === 'medium' && <span className="badge badge--yellow">Standard</span>}
                                        {ticket.priority === 'low' && <span className="badge badge--gray">Low</span>}
                                    </td>
                                    <td>
                                        {ticket.status === 'resolved' && <span className="badge badge--green">Resolved</span>}
                                        {ticket.status === 'in_progress' && <span className="badge badge--blue">In Progress</span>}
                                        {ticket.status === 'new' && <span className="badge badge--yellow">Received</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Report Issue Modal ── */}
            {isModalOpen && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => setIsModalOpen(false)}
                    style={{ background: 'rgba(15,23,42,0.65)' }}
                >
                    <div
                        className="dashboard-modal"
                        style={{
                            background: '#fff', padding: '3rem',
                            boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)',
                            animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1)',
                            maxWidth: 500, width: '100%',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem' }}>
                            <div>
                                <div style={{ display: 'inline-flex', padding: '10px', borderRadius: '14px', background: 'rgba(99,102,241,0.08)', color: '#6366f1', marginBottom: '1rem' }}>
                                    <Wrench size={22} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#1a1d2e' }}>
                                    Report an Issue
                                </h3>
                                <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginTop: '6px', fontWeight: 500 }}>
                                    Submit a maintenance request to your landlord
                                </p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '10px', borderRadius: '12px', display: 'flex' }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '8px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Issue Summary
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. Leaking kitchen faucet"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    style={{ borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #e2e8f0', fontSize: '1rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '8px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Description
                                </label>
                                <textarea
                                    placeholder="Describe the issue in detail…"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    style={{ width: '100%', minHeight: '110px', padding: '1rem 1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0', fontFamily: 'inherit', fontSize: '1rem', fontWeight: 500, resize: 'vertical', background: '#f8fafc', color: '#1a1d2e', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '8px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Priority
                                </label>
                                <select
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                                    style={{ width: '100%', padding: '1rem 1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '1rem', background: '#f8fafc', fontWeight: 600, cursor: 'pointer', appearance: 'none', boxSizing: 'border-box' }}
                                >
                                    <option value="low">Low — Not urgent</option>
                                    <option value="medium">Medium — Needs attention soon</option>
                                    <option value="high">High — Emergency / urgent</option>
                                </select>
                            </div>

                            {/* Photo upload */}
                            <div>
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '8px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Photos (optional)
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handlePhotoChange}
                                />
                                {photoPreviews.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                                        {photoPreviews.map((src, idx) => (
                                            <div key={idx} style={{ position: 'relative', width: 80, height: 80 }}>
                                                <img src={src} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0' }} />
                                                <button
                                                    type="button"
                                                    onClick={() => removePhoto(idx)}
                                                    style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}
                                                >
                                                    <X size={11} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (fileInputRef.current) {
                                                fileInputRef.current.removeAttribute('capture');
                                                fileInputRef.current.click();
                                            }
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0.625rem 1rem', borderRadius: '10px', border: '1.5px dashed #cbd5e1', background: '#f8fafc', color: '#64748b', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        <ImagePlus size={15} /> Choose File
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (fileInputRef.current) {
                                                fileInputRef.current.setAttribute('capture', 'environment');
                                                fileInputRef.current.click();
                                            }
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0.625rem 1rem', borderRadius: '10px', border: '1.5px dashed #cbd5e1', background: '#f8fafc', color: '#64748b', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        <Camera size={15} /> Take Photo
                                    </button>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '1rem', borderTop: '1px solid var(--color-border-subtle)' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)} style={{ borderRadius: '12px', padding: '0.875rem 1.5rem', fontWeight: 700 }}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isSubmitting || !title}
                                    style={{ borderRadius: '12px', padding: '0.875rem 2rem', fontWeight: 800, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {isSubmitting
                                        ? <><Loader2 size={15} className="animate-spin" /> Submitting…</>
                                        : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(24px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
            `}</style>
        </div>
    );
}
